/**
 * One client: what it is, which template it follows, its signing key, and how to be rid of it.
 *
 * The editable values read as text until you reach for them. Making every field a permanent
 * input would say these change often; they change about twice a year.
 */
import { useState } from 'react';
import { ApiError, api, type Client, type KeystoreResult, type Template } from '../api';
import { EditClient } from './EditClient';
import { IconFit } from './IconFit';
import { AppStoreKey } from './AppStoreKey';
import { IosSigning } from './IosSigning';
import { GenerateKey, KeystoreSummary } from './KeystoreChoice';

export function ClientPanel({ client, template, isAdmin, onChanged, onToast }: {
  client: Client;
  template: Template | null;
  /** Only an admin may replace signing material that already exists. */
  isAdmin: boolean;
  onChanged: () => void;
  onToast: (message: string) => void;
}) {
  const [editing, setEditing] = useState<'ref' | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showKeystore, setShowKeystore] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [showAppStore, setShowAppStore] = useState(false);
  const [iconScale, setIconScale] = useState(client.iconScale);
  const [iconTrim, setIconTrim] = useState(client.iconTrim);

  const followed = client.templateRef ?? template?.defaultRef ?? 'main';
  const refs = template ? [...template.branches, ...template.tags] : [];

  const save = (patch: Parameters<typeof api.patchClient>[1], done: string) => {
    api.patchClient(client.slug, patch)
      .then(() => { onToast(done); onChanged(); })
      .catch((failure: ApiError) => onToast(failure.message));
    setEditing(null);
  };

  return (
    <>
      <dl className="facts">
        <div className="fact">
          <dt>Package</dt>
          <dd>{client.packageName}</dd>
        </div>
        <div className="fact">
          <dt>Backend</dt>
          <dd>{client.appDomain.replace(/^https?:\/\//, '')}</dd>
        </div>
        <div className="fact">
          <dt>Next version</dt>
          <dd>{client.nextVersionCode}</dd>
        </div>
        <div className="fact">
          <dt>Template</dt>
          <dd>
            {editing === 'ref' && refs.length > 0 ? (
              <select
                id={`ref-${client.slug}`}
                defaultValue={client.templateRef ?? ''}
                autoFocus
                onChange={(event) => save({ templateRef: event.target.value || null },
                  event.target.value ? `Follows ${event.target.value}` : 'Follows the default branch')}
                onBlur={() => setEditing(null)}
              >
                <option value="">{template?.defaultRef ?? 'main'} (default)</option>
                {refs.map((ref) => <option key={ref} value={ref}>{ref}</option>)}
              </select>
            ) : (
              <button type="button" className="plain" onClick={() => setEditing('ref')}>
                {followed}
              </button>
            )}
          </dd>
        </div>
        <div className="fact">
          <dt>About page</dt>
          <dd>{client.aboutMenuTitle ?? 'found automatically'}</dd>
        </div>
        <div className="fact">
          <dt>Icon fit</dt>
          <dd>
            <button type="button" className="plain" onClick={() => setShowIcon((open) => !open)}>
              {client.iconScale === 100 && !client.iconTrim ? 'default' : `${client.iconScale}%${client.iconTrim ? ', trimmed' : ''}`}
            </button>
          </dd>
        </div>
        <div className="fact prose">
          <dt>iOS signing</dt>
          <dd className={client.hasIosSigning ? '' : 'is-missing'}>
            {client.hasIosSigning ? 'stored' : 'not set up'}
          </dd>
        </div>
        <div className="fact prose">
          <dt>App Store delivery</dt>
          <dd className={client.hasAppStoreKey ? '' : 'is-missing'}>
            {client.hasAppStoreKey ? 'key stored' : 'by hand'}
          </dd>
        </div>
        <div className="fact prose">
          <dt>Signing key</dt>
          <dd className={client.hasKeystore ? '' : 'is-missing'}>
            {client.hasKeystore ? 'stored' : 'not yet made'}
          </dd>
        </div>
      </dl>

      <div className="actions" style={{ marginTop: 20 }}>
        <button type="button" className="btn quiet" onClick={() => setShowDetails((open) => !open)}>
          {showDetails ? 'Cancel' : 'Edit details'}
        </button>
        <button type="button" className={client.hasIosSigning ? 'btn quiet' : 'btn'}
                onClick={() => setShowIos((open) => !open)}>
          {showIos ? 'Cancel' : client.hasIosSigning ? 'iOS signing' : 'Set up iOS signing'}
        </button>
        <button type="button" className="btn quiet"
                onClick={() => setShowAppStore((open) => !open)}>
          {showAppStore ? 'Cancel' : client.hasAppStoreKey ? 'App Store delivery' : 'Set up App Store delivery'}
        </button>
        <button type="button" className={client.hasKeystore ? 'btn quiet' : 'btn'}
                onClick={() => setShowKeystore((open) => !open)}>
          {showKeystore ? 'Cancel' : client.hasKeystore ? 'Signing key' : 'Set up the signing key'}
        </button>
      </div>

      {showDetails && (
        <EditClient
          client={client}
          onSaved={() => { setShowDetails(false); onChanged(); }}
          onCancel={() => setShowDetails(false)}
          onToast={onToast}
        />
      )}

      {showIcon && (
        <section className="section">
          <div className="section-head">
            <h2>How the logo lands on the phone</h2>
            <span className="note">The next build uses this</span>
          </div>
          <IconFit
            slug={client.slug}
            scale={iconScale}
            trim={iconTrim}
            background={client.iconBackground}
            onScale={setIconScale}
            onTrim={setIconTrim}
          />
          <div className="actions" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="btn"
              disabled={iconScale === client.iconScale && iconTrim === client.iconTrim}
              onClick={() => save({ iconScale, iconTrim }, 'The next build will use this icon fit')}
            >
              Save the fit
            </button>
            <button
              type="button"
              className="btn quiet"
              onClick={() => { setIconScale(client.iconScale); setIconTrim(client.iconTrim); setShowIcon(false); }}
            >
              Cancel
            </button>
          </div>
          <p className="hint" style={{ marginTop: 10 }}>Applies to the next build.</p>
        </section>
      )}

      {showIos && (
        <IosSigning client={client} isAdmin={isAdmin} onToast={onToast} onChanged={onChanged} />
      )}

      {showAppStore && (
        <AppStoreKey client={client} isAdmin={isAdmin} onToast={onToast} onChanged={onChanged} />
      )}

      {showKeystore && !client.hasKeystore && (
        <GenerateKey slug={client.slug} onToast={onToast}
                     onDone={() => { setShowKeystore(false); onChanged(); }} />
      )}
      {showKeystore && (
        <KeystoreForm client={client} onToast={onToast}
                      onDone={() => { setShowKeystore(false); onChanged(); }} />
      )}

    </>
  );
}

/**
 * Removing a client.
 *
 * Typing the name is the confirmation. The signing key is deliberately kept, because Play binds
 * a package to its upload key permanently -- deleting a client by mistake is recoverable, and
 * deleting the key is not.
 */
export function RemoveClient({ client, onToast, onRemoved }: {
  client: Client;
  onToast: (message: string) => void;
  onRemoved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <section className="danger-zone">
        <h2>Remove this client</h2>
        <p>
          Deletes the client and every build it produced. The signing key is kept.
        </p>
        <button type="button" className="btn quiet" onClick={() => setOpen(true)}>
          Remove {client.appName}
        </button>
      </section>
    );
  }

  return (
    <section className="danger-zone">
      <h2>Remove {client.appName}?</h2>
      <p>
        This cannot be undone. Type <strong className="mono">{client.slug}</strong> to confirm.
      </p>
      <div className="confirm">
        <input
          id={`confirm-${client.slug}`}
          type="text"
          value={typed}
          autoFocus
          placeholder={client.slug}
          onChange={(event) => setTyped(event.target.value)}
          style={{ fontFamily: 'var(--mono)', padding: '9px 11px', borderRadius: 3, border: '1px solid var(--rule)', background: 'var(--raised)' }}
        />
        <div className="actions">
          <button
            type="button"
            className="btn danger"
            disabled={typed.trim() !== client.slug || busy}
            onClick={() => {
              setBusy(true);
              api.deleteClient(client.slug)
                .then((result) => {
                  onToast(`Removed ${client.slug}: ${result.buildsRemoved} build`
                    + `${result.buildsRemoved === 1 ? '' : 's'}`
                    + `${result.keystoreKeptAt ? ', signing key kept' : ''}`);
                  onRemoved();
                })
                .catch((failure: ApiError) => onToast(failure.message))
                .finally(() => setBusy(false));
            }}
          >
            {busy ? 'Removing' : 'Remove permanently'}
          </button>
          <button type="button" className="btn quiet" onClick={() => { setOpen(false); setTyped(''); }}>
            Keep it
          </button>
        </div>
      </div>
    </section>
  );
}

function KeystoreForm({ client, onToast, onDone }: {
  client: Client;
  onToast: (message: string) => void;
  onDone: () => void;
}) {
  const [errors, setErrors] = useState<string[]>([]);
  const [result, setResult] = useState<KeystoreResult | null>(null);
  const [saving, setSaving] = useState(false);

  if (result) return <KeystoreSummary result={result} onDone={onDone} />;

  return (
    <form
      className="form section"
      onSubmit={(event) => {
        event.preventDefault();
        setErrors([]);
        setSaving(true);
        api.uploadKeystore(client.slug, new FormData(event.currentTarget))
          .then((uploaded) => {
            setResult(uploaded);
            onToast(uploaded.replacedPrevious ? 'Signing key replaced' : 'Signing key stored');
          })
          .catch((failure: ApiError) => setErrors(failure.errors?.length ? failure.errors : [failure.message]))
          .finally(() => setSaving(false));
      }}
    >
      <p style={{ margin: 0, color: 'var(--ink-2)', maxWidth: '58ch' }}>
        {client.hasKeystore
          ? 'Replacing the key only makes sense if Play already expects a different one. The current key is kept.'
          : 'If this app is already on Play, upload the key it was first signed with.'}
      </p>

      {errors.length > 0 && (
        <div className="errors">{errors.map((message) => <span key={message}>{message}</span>)}</div>
      )}

      <div className="field">
        <label htmlFor={`ks-file-${client.slug}`}>Keystore file</label>
        <input id={`ks-file-${client.slug}`} name="keystore" type="file" accept=".jks,.keystore,.p12,.pfx" required />
      </div>
      <div className="pair">
        <div className="field">
          <label htmlFor={`ks-alias-${client.slug}`}>Key alias</label>
          <input id={`ks-alias-${client.slug}`} name="keyAlias" type="text" required placeholder="upload" />
        </div>
        <div className="field">
          <label htmlFor={`ks-store-${client.slug}`}>Store password</label>
          <input id={`ks-store-${client.slug}`} name="storePassword" type="password" required />
        </div>
      </div>
      <div className="field">
        <label htmlFor={`ks-key-${client.slug}`}>Key password</label>
        <input id={`ks-key-${client.slug}`} name="keyPassword" type="password" />
        <span className="hint">Leave blank if it matches the store password. PKCS12 always uses one.</span>
      </div>

      <div className="actions">
        <button className="btn" type="submit" disabled={saving}>
          {saving ? 'Checking the key' : client.hasKeystore ? 'Replace key' : 'Store key'}
        </button>
        <button className="btn quiet" type="button" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}
